/* eslint-disable react/prop-types -- props validated via ProfilePresenterProps (TypeScript) */
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { H1, Tabs, Tab } from '../../lib/components'
import { GeneralForm } from './general-form'
import { KYCContainer } from '../KYC/kyc-container'
import { BankDetailsContainer } from './bank-details-container'
import { useNavigate, useParams } from 'react-router-dom'
import { setPreviousUrl } from '../../lib/utils/helpers'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { RootState } from '../../store'
import { investorGeneralSchema } from '../../lib/utils/schema'
import { KYCDocumentsContainer } from './kyc-documents-container'
import { BasicDetailsProfile } from '../../lib/utils/onboarding'

type ProfileTabId = 'general' | 'kyc' | 'kyc-documents' | 'bank-details'

interface ProfilePresenterProps {
  initialValues: {
    name: string
    countryOfResidence: string
    email: string
    phoneNumber: string
    citizenship: string
    countryCode: string
    passportNumber: string
    whatsappConsent: boolean
  }
  handleGeneralFormSubmit: (val: object) => Promise<void>
  investorGeneralSchema: typeof investorGeneralSchema
  acceptedNDA: boolean
  isKycStateSynced?: boolean
  isGeneralProfileReadOnly?: boolean
  basicDetailsProfile?: BasicDetailsProfile | null
}

const ProfilePresenter = ({
  initialValues,
  handleGeneralFormSubmit,
  investorGeneralSchema,
  acceptedNDA,
  isKycStateSynced = true,
  isGeneralProfileReadOnly = false,
  basicDetailsProfile = null,
}: ProfilePresenterProps) => {
  const { authToken, kycStatus } = useSelector((state: RootState) => state.investor)
  const navigate = useNavigate()
  const params = useParams()
  const { profileTab } = params

  const tabDefinitions = useMemo(() => {
    const tabs: { id: ProfileTabId; title: string; content: React.ReactNode }[] = [
      {
        id: 'general',
        title: 'General',
        content: (
          <GeneralForm
            initialValues={initialValues}
            handleFormSubmit={handleGeneralFormSubmit}
            validationSchema={investorGeneralSchema}
            isReadOnly={isGeneralProfileReadOnly}
            basicDetailsProfile={basicDetailsProfile}
          />
        ),
      },
    ]

    tabs.push({
      id: 'kyc',
      title: 'KYC',
      content: <KYCContainer />,
    })

    if (kycStatus !== 'NOT_STARTED') {
      tabs.push({
        id: 'kyc-documents',
        title: 'KYC Documents',
        content: <KYCDocumentsContainer />,
      })
    }

    if (kycStatus === 'APPROVED') {
      tabs.push({
        id: 'bank-details',
        title: 'Bank Details',
        content: <BankDetailsContainer />,
      })
    }

    return tabs
  }, [
    initialValues,
    handleGeneralFormSubmit,
    investorGeneralSchema,
    isGeneralProfileReadOnly,
    basicDetailsProfile,
    kycStatus,
  ])

  const activeIndex = useMemo(() => {
    if (!profileTab) return 0
    const index = tabDefinitions.findIndex((tab) => tab.id === profileTab)
    return index >= 0 ? index : 0
  }, [profileTab, tabDefinitions])

  useEffect(() => {
    if (!profileTab) {
      navigate('/profile/general', { replace: true })
      return
    }

    const tabExists = tabDefinitions.some((tab) => tab.id === profileTab)
    if (!tabExists) {
      navigate('/profile/general', { replace: true })
      return
    }

    const needsNdaBeforeKyc =
      isKycStateSynced && !acceptedNDA && (kycStatus === 'NOT_STARTED' || kycStatus === '')

    if (profileTab === 'kyc' && needsNdaBeforeKyc) {
      setPreviousUrl('/profile/kyc')
      navigate('/accept-NDA', { replace: true })
    }
  }, [profileTab, tabDefinitions, acceptedNDA, isKycStateSynced, kycStatus, navigate])

  useEffect(() => {
    if (profileTab === 'general') {
      logCrmEvent(EngagementEvents.CLICKED_PROFILE_PAGE, {
        Authorization: authToken ?? '',
      })
    }

    if (profileTab === 'kyc') {
      logCrmEvent(EngagementEvents.CLICKED_KYC_PAGE, {
        Authorization: authToken ?? '',
      })
    }
  }, [profileTab, authToken])

  const handleChange = (index: number) => {
    const tab = tabDefinitions[index]
    if (!tab || tab.id === profileTab) return

    const needsNdaBeforeKyc =
      isKycStateSynced && !acceptedNDA && (kycStatus === 'NOT_STARTED' || kycStatus === '')

    if (tab.id === 'kyc' && needsNdaBeforeKyc) {
      setPreviousUrl('/profile/kyc')
      navigate('/accept-NDA', { replace: true })
      return
    }

    navigate(`/profile/${tab.id}`, { replace: true })
  }

  return (
    <>
      <H1 text={isGeneralProfileReadOnly ? 'Profile' : 'Edit profile'} />
      <div className='profile-tab-container'>
        <Tabs active={activeIndex} handleChange={handleChange} dataTestId='profile-tabs'>
          {tabDefinitions.map((tab) => (
            <Tab key={tab.id} title={tab.title}>
              {tab.content}
            </Tab>
          ))}
        </Tabs>
      </div>
    </>
  )
}

export { ProfilePresenter }
